import type { QueryClient } from "@tanstack/react-query";
import { create } from "zustand";
import { useAuthStoreV1 } from "./authStore_v1";
import { registerOrgScopedReset } from "@/lib/org-scope";
import {
	acceptHealProposal,
	acceptOrgConfigChange,
	type ActivityStatus,
	type ExecuteResult,
	type OrgConfigApplyResult,
	type OrgConfigVerdictResult,
	rejectOrgConfigChange,
	revertOrgConfigChange,
	submitOrgConfigVerdict,
	type ExecuteStreamEvent,
	type HealCompleteEvent,
	type HealStage,
	type IntentContract,
	type IrPlanBody,
	isHealComplete,
	isIrPlan,
	type PlanBody,
	type PlanContract,
	type ProposedHeal,
	rejectHealProposal,
	type RuntimeActivityEvent,
	streamExecutePlan,
	streamHealRun,
	streamChatMessage,
	stopChatSession,
	type DestructiveAction,
	type TestPlan,
	type TestingChatEvent,
	toolResultOutcome,
} from "@/services/testing";
import { owningScopeForPlan } from "@/utils/testing/sessions";

/**
 * Single in-memory store for the Testing page — chat/planning state, the live SSE stream, and the
 * page's view state (selected plan, scope, completed runs, panel toggles).
 *
 * Deliberately a plain Zustand store with NO `persist` middleware. Because the store lives at module
 * scope (outside React), it survives route/tab navigation natively — which is the whole reason the old
 * component-local useState + sessionStorage + backend "reconnect" machinery existed. And because it is
 * in-memory only, a hard reload / fresh login starts CLEAN (no stale state carried across a login).
 * `ensureUser` additionally resets everything if a different user takes over the tab.
 */

export interface RenderedMessage {
	id: string;
	role: "user" | "assistant" | "tool";
	content: string;
	tool?: {
		name: string;
		args: Record<string, unknown>;
		ok?: boolean;
		data?: unknown;
		error?: string | null;
	};
	proposedPlan?: TestPlan;
	planContract?: PlanContract;
	/** The planner's structured reading of the ask — show it before the user approves. */
	intentContract?: IntentContract;
	/** Parts of the ask the plan does not verify. Advisory; never blocks approval. */
	intentGaps?: string[];
	metadata?: Record<string, unknown>;
	/** Activity snapshot captured when this message's stream ended — shows tool calls inline. */
	chatActivity?: TestingChatActivity[];
	/** When the message was created (epoch ms). Drives the per-turn timestamp in chat. */
	at?: number;
}

export interface TestingChatActivity {
	id: string;
	kind:
		| "phase"
		| "tool"
		| "result"
		| "plan"
		| "message"
		| "error"
		| RuntimeActivityEvent["kind"];
	label: string;
	detail?: string;
	/** `warning` = the call returned but produced no usable evidence (see `toolResultOutcome`). */
	status?: ActivityStatus;
	tool?: string;
	data?: unknown;
	at: number;
}

export interface ChatState {
	messages: RenderedMessage[];
	lastProposedPlanId: string | null;
	phase: string | null;
	activity: TestingChatActivity[];
}

export const EMPTY_CHAT_STATE: ChatState = {
	messages: [],
	lastProposedPlanId: null,
	phase: null,
	activity: [],
};

/**
 * Lifecycle of a heal PROPOSAL — not of a run.
 * `proposed` = a patch set is waiting on the user; `declined` = the healer found nothing worth
 * proposing (see `healDeclineReason`).
 */
export type HealStatus =
	| "idle"
	| "running"
	| "proposed"
	| "declined"
	| "error"
	/** The heal endpoint is disabled server-side (HTTP 410 on open). */
	| "disabled";

export type CompletedRun = {
	result: ExecuteResult;
	/** The plan this run executed. Its result card renders directly under this plan's card within the
	 *  plan's session (and drives "Run again"), so a run can never surface under a different plan. */
	planId: string;
};

/** React-setState-style arg: a value, or an updater `(prev) => next`. */
type Setter<T> = T | ((prev: T) => T);
function apply<T>(arg: Setter<T>, prev: T): T {
	return typeof arg === "function" ? (arg as (p: T) => T)(prev) : arg;
}

interface TestingStore {
	// ── who this state belongs to (reset on user change) ──
	uid: string | null;

	// ── chat / planning ──
	byScope: Record<string, ChatState>;
	sessionIds: Record<string, string | null>;
	streaming: boolean;
	error: string | null;
	/** Increments each time a chat stream completes — subscribers refresh plan list / canvas. */
	streamEndTick: number;
	/** Non-reactive stream bookkeeping (kept here so it survives navigation). */
	_abort: (() => void) | null;
	_currentAsstMsgId: string | null;
	/** Scope the live stream writes to — so cancel()/the hook can bind streaming
	 *  state to the right chat even after a mid-stream scope promotion. */
	_streamScope: string | null;
	/** Redirect for a scope that was renamed (moveScope) while its stream was still
	 *  live: late events targeting the old key land in the new one instead of
	 *  resurrecting a dead empty scope. Keyed old→new. */
	_scopeRedirect: Record<string, string>;

	// ── execution run stream (server-side browser job; the SSE survives tab nav here) ──
	execEvents: ExecuteStreamEvent[];
	execResult: ExecuteResult | null;
	execStreaming: boolean;
	execError: Error | null;
	_execAbort: (() => void) | null;
	_execRunId: string | null;
	/** The plan the current `execResult` belongs to — so a plan edit can tell whether the live result
	 *  it is about to clear is actually this plan's (a run ran the OLD spec → stale on edit). */
	_execPlanId: string | null;

	// ── self-heal PROPOSAL stream ──
	// A heal no longer re-runs and re-verdicts the plan. It proposes a bounded IR patch set that a
	// human accepts or rejects; the source run's verdict is never touched. So there is no
	// `healResult: ExecuteResult` — treating a proposal as a second run is what made the old UI
	// claim a failed test had "healed" when nothing had been applied yet.
	healEvents: ExecuteStreamEvent[];
	healStreaming: boolean;
	healError: Error | null;
	healStatus: HealStatus;
	/** The pending, un-accepted patch set. Cleared on accept/reject (server clears it too). */
	healProposal: ProposedHeal | null;
	/** Plan the pending proposal belongs to — needed to accept/reject it. */
	healPlanId: string | null;
	healNotes: string[];
	/** Why the healer declined to propose anything (status === "declined"). */
	healDeclineReason: string | null;
	healStage: HealStage | null;
	healTriageCategory: string | null;
	/** In-flight accept/reject. */
	healDeciding: boolean;
	_healAbort: (() => void) | null;
	_healRunId: string | null;

	// ── org-config CONSENT loop (apply a proven ORG_CONFIG fix, then re-run to verify the diagnosis) ──
	/** In-flight apply/revert of an org-config change. */
	orgConfigDeciding: boolean;
	/** Result of the last apply (or revert), for the card to render. */
	orgConfigApplyResult: OrgConfigApplyResult | null;
	/** The diagnosis verdict once the re-run completes (was our ORG_CONFIG output correct?). */
	orgConfigVerdict: OrgConfigVerdictResult | null;
	/** When set, the next execute-complete for this plan submits its runId as the verification run. */
	_orgConfigPendingVerdict: { planId: string; changeId: string } | null;

	// ── page / view state ──
	selectedPlanId: string | null;
	/** When set, typed chat messages EDIT this plan (activePlanId=this). When null, a typed message
	 *  creates a NEW plan. Toggled by the per-plan "Edit" button; the input shows a locked edit chip. */
	editingPlanId: string | null;
	activeChatScope: string;
	completedRuns: CompletedRun[];
	historyPlanId: string | null;
	liveExpanded: boolean;

	// ── chat actions ──
	updateScope: (key: string, updater: (s: ChatState) => ChatState) => void;
	appendActivity: (key: string, entry: Omit<TestingChatActivity, "id" | "at">) => void;
	send: (scopeKey: string, text: string, activePlanId?: string | null) => void;
	cancel: () => void;
	moveScope: (from: string, to: string) => void;
	resetScope: (key: string) => void;
	/**
	 * Populate a scope's thread from server-persisted messages on load — so a conversation renders its
	 * cards after a reload, not just its grouping. Keyed by the SAME scope the live promote uses
	 * (`plan:<firstPlanId>`), with the server `sessionId` recorded so a follow-up message continues that
	 * conversation. No-op if the scope is already in memory, so it never clobbers a live thread.
	 */
	hydrateScope: (
		scopeKey: string,
		sessionId: string,
		messages: RenderedMessage[],
		lastProposedPlanId: string | null,
	) => void;
	postRepairPrompt: (scopeKey: string, runId: string, text: string) => void;
	/**
	 * Merge a fresh VERIFY verdict into the proposal that is currently shown in chat, so the
	 * VerificationBadge flips to Verified/Flaky the instant the headless run returns. The card renders
	 * the SSE-streamed `proposedPlan`, not a refetched query, so invalidating caches alone never reached
	 * it — this patches the in-store snapshot directly.
	 */
	setPlanVerification: (planId: string, verification: PlanVerification) => void;


	// ── run actions ──
	execute: (
		planId: string,
		opts: { headed?: boolean; live?: boolean } | undefined,
		queryClient: QueryClient,
	) => void;
	execAbort: () => void;
	execReset: () => void;
	/**
	 * Drop every run recorded against a plan, and clear the live result if it is that plan's. Called
	 * when a plan's spec CHANGES under the same id (a `plan_updated` edit): a prior run executed the old
	 * spec, so its verdict is stale — without this it re-anchors under the edited plan card, showing a
	 * "passed" for content that changed and was never re-run (the test_96-card-on-test_7878 bug).
	 */
	invalidateRunsForPlan: (planId: string) => void;

	// ── heal-proposal actions ──
	heal: (runId: string, queryClient: QueryClient) => void;
	healAbort: () => void;
	healReset: () => void;
	/** Apply the pending proposal to the plan. Always clears local proposal state afterwards. */
	acceptHeal: (planId: string, queryClient: QueryClient) => Promise<void>;
	/** Discard the pending proposal. Always clears local proposal state afterwards. */
	rejectHeal: (planId: string, queryClient: QueryClient) => Promise<void>;

	// ── org-config consent actions ──
	/** Consent to apply the org-config change, then re-run the plan; the re-run's verdict is recorded. */
	acceptOrgConfig: (planId: string, changeId: string, queryClient: QueryClient) => Promise<void>;
	/** Dismiss the org-config proposal without applying it. */
	rejectOrgConfig: (planId: string, changeId: string) => Promise<void>;
	/** Undo a previously-applied org-config change. */
	revertOrgConfig: (planId: string, changeId: string) => Promise<void>;
	/** Clear org-config decision state (on run reset / switching plans). */
	orgConfigReset: () => void;

	// ── page setters (accept a value or an updater, like useState) ──
	setSelectedPlanId: (v: Setter<string | null>) => void;
	setActiveChatScope: (v: Setter<string>) => void;
	setCompletedRuns: (v: Setter<CompletedRun[]>) => void;
	setEditingPlanId: (v: Setter<string | null>) => void;
	setHistoryPlanId: (v: Setter<string | null>) => void;
	setLiveExpanded: (v: Setter<boolean>) => void;

	/** Reset all testing state when a different user takes over the tab (keeps a fresh login clean). */
	ensureUser: (uid: string) => void;
}

const FRESH = {
	byScope: {} as Record<string, ChatState>,
	sessionIds: {} as Record<string, string | null>,
	streaming: false,
	error: null as string | null,
	streamEndTick: 0,
	_abort: null as (() => void) | null,
	_currentAsstMsgId: null as string | null,
	_streamScope: null as string | null,
	_scopeRedirect: {} as Record<string, string>,
	execEvents: [] as ExecuteStreamEvent[],
	execResult: null as ExecuteResult | null,
	execStreaming: false,
	execError: null as Error | null,
	_execAbort: null as (() => void) | null,
	_execRunId: null as string | null,
	_execPlanId: null as string | null,
	healEvents: [] as ExecuteStreamEvent[],
	healStreaming: false,
	healError: null as Error | null,
	healStatus: "idle" as HealStatus,
	healProposal: null as ProposedHeal | null,
	healPlanId: null as string | null,
	healNotes: [] as string[],
	healDeclineReason: null as string | null,
	healStage: null as HealStage | null,
	healTriageCategory: null as string | null,
	healDeciding: false,
	_healAbort: null as (() => void) | null,
	_healRunId: null as string | null,
	orgConfigDeciding: false,
	orgConfigApplyResult: null as OrgConfigApplyResult | null,
	orgConfigVerdict: null as OrgConfigVerdictResult | null,
	_orgConfigPendingVerdict: null as { planId: string; changeId: string } | null,
	selectedPlanId: null as string | null,
	editingPlanId: null as string | null,
	activeChatScope: "",
	completedRuns: [] as CompletedRun[],
	historyPlanId: null as string | null,
	liveExpanded: false,
};

// ── Selection persistence (survives a full page reload) ──────────────────────
// Mirrors multiAgentStore's pattern: a tenant-scoped localStorage entry holding
// the MINIMAL selection (the selected plan id). Restoring it on load re-enables
// the runs poll for that plan, so an in-progress run RECONNECTS after a hard
// refresh instead of vanishing. Chat threads stay in-memory (the run + plan
// detail are rebuilt from the server).
function testingTenant(): { userId: string; orgId: string } | null {
	const s = useAuthStoreV1.getState();
	const userId = s.user?.id;
	const orgId = s.selectedOrg?.id;
	return userId && orgId ? { userId, orgId } : null;
}
function testingSelectionKey(userId: string, orgId: string): string {
	return `testing:selection:${userId}:${orgId}`;
}
export function readPersistedTestingSelection(): { selectedPlanId: string | null } {
	const t = testingTenant();
	if (!t) return { selectedPlanId: null };
	try {
		const raw = localStorage.getItem(testingSelectionKey(t.userId, t.orgId));
		if (!raw) return { selectedPlanId: null };
		const v = JSON.parse(raw);
		return { selectedPlanId: typeof v.selectedPlanId === "string" ? v.selectedPlanId : null };
	} catch {
		return { selectedPlanId: null };
	}
}
function writePersistedTestingSelection(selectedPlanId: string | null): void {
	const t = testingTenant();
	if (!t) return;
	try {
		localStorage.setItem(testingSelectionKey(t.userId, t.orgId), JSON.stringify({ selectedPlanId }));
	} catch {
		/* quota / SSR safety */
	}
}

export const useTestingStore = create<TestingStore>((set, get) => ({
	uid: null,
	...FRESH,

	updateScope: (key, updater) =>
		set((s) => {
			// Follow a promotion redirect so a stream still writing to its original
			// (now-renamed) scope lands in the new scope instead of resurrecting a
			// deleted empty one.
			const realKey = s._scopeRedirect[key] ?? key;
			return {
				byScope: { ...s.byScope, [realKey]: updater(s.byScope[realKey] ?? EMPTY_CHAT_STATE) },
			};
		}),

	appendActivity: (key, entry) =>
		get().updateScope(key, (state) => ({
			...state,
			activity: [
				// Unique id (not Date.now()-index, which collides once the list saturates
				// at 20 entries) — these are React keys in the live activity list.
				...state.activity.slice(-19),
				{ ...entry, id: crypto.randomUUID(), at: Date.now() },
			],
			phase: entry.kind === "phase" ? entry.label : state.phase,
		})),

	send: (scopeKey, text, activePlanId) => {
		if (!text.trim()) return;
		// Send-while-streaming PREEMPTS the in-flight run instead of dropping the
		// message. The old `return`-on-streaming silently swallowed anything typed
		// mid-run (a message that vanished with no reply). This mirrors the
		// testing-service, which auto-cancels the active run when the next
		// /messages POST arrives (run preemption) — the Claude.ai / ChatGPT UX.
		// cancel() aborts the current SSE stream and snapshots its partial activity
		// onto the superseded message before we start the new one.
		if (get().streaming) get().cancel();
		set({ error: null, streaming: true });
		const { updateScope, appendActivity } = get();
		const streamScopeKey = scopeKey;
		// Unique ids (not Date.now(), which collides on two sends in the same ms —
		// these are React keys AND the match key for streamed updates).
		const userMsgId = `u-${crypto.randomUUID()}`;
		const asstMsgId = `a-${crypto.randomUUID()}`;
		set({ _currentAsstMsgId: asstMsgId, _streamScope: streamScopeKey });
		// One timestamp for the exchange: the question and its reply belong to the same moment, and the
		// chat shows a single time per turn rather than one per message.
		const sentAt = Date.now();
		updateScope(streamScopeKey, (state) => ({
			...state,
			messages: [
				...state.messages,
				{ id: userMsgId, role: "user", content: text, at: sentAt },
				{ id: asstMsgId, role: "assistant", content: "", at: sentAt },
			],
			phase: "Researching your org",
			// Reset activity for this new response — prevents stale activity from a cancelled previous
			// stream bleeding into the next message's activity display.
			activity: [
				{
					id: crypto.randomUUID(),
					kind: "phase",
					label: "Researching your org",
					detail: activePlanId ? "Looking up plan context" : "Understanding your request",
					status: "running",
					at: Date.now(),
				},
			],
		}));

		let didEmit = false;

		const abort = streamChatMessage({
			content: text,
			sessionId: get().sessionIds[streamScopeKey] ?? null,
			activePlanId,
			onEvent: (evt: TestingChatEvent) => {
				if (evt.type === "session" && "session_id" in evt) {
					const sid = (evt as { session_id: string }).session_id;
					set((s) => ({ sessionIds: { ...s.sessionIds, [streamScopeKey]: sid } }));
					return;
				}
				if (evt.type === "runtime_activity") {
					appendActivity(streamScopeKey, normalizeRuntimeActivity(evt));
					return;
				}
				if (evt.type === "thinking") {
					appendActivity(streamScopeKey, {
						kind: "phase",
						label: evt.phase || "Thinking through the plan",
						status: "running",
					});
					return;
				}
				if (evt.type === "tool_call") {
					appendActivity(streamScopeKey, {
						kind: "tool",
						label: compactActivityText(evt.say || `Calling ${evt.tool}`) ?? `Calling ${evt.tool}`,
						detail: evt.tool,
						status: "running",
					});
					updateScope(streamScopeKey, (state) => ({
						...state,
						messages: state.messages.map((m) =>
							m.id === asstMsgId
								? { ...m, tool: { name: evt.tool, args: evt.args }, content: m.content || evt.say }
								: m,
						),
					}));
					return;
				}
				if (evt.type === "tool_result") {
					// `ok: true` only means the call returned. A payload that says the tool was
					// unavailable, that the agent abstained, or that the FLS graph is empty grounded
					// NOTHING — showing it green would claim evidence the planner never gathered.
					const outcome = toolResultOutcome(evt.ok, evt.data, evt.error);
					appendActivity(streamScopeKey, {
						kind: "result",
						label:
							outcome.status === "error"
								? `Failed ${evt.tool}`
								: outcome.status === "warning"
									? `No result from ${evt.tool}`
									: `Finished ${evt.tool}`,
						detail: compactActivityText(
							outcome.reason ?? evt.error ?? summarizeToolData(evt.data) ?? "",
						),
						status: outcome.status,
					});
					updateScope(streamScopeKey, (state) => ({
						...state,
						messages: state.messages.map((m) =>
							m.id === asstMsgId
								? {
										...m,
										tool: m.tool
											? { ...m.tool, ok: evt.ok, data: evt.data, error: evt.error }
											: { name: evt.tool, args: {}, ok: evt.ok, data: evt.data, error: evt.error },
									}
								: m,
						),
					}));
					return;
				}
				if (evt.type === "plan_proposed") {
					const plan = buildProposedPlan(evt.plan_id, evt.name, evt.steps, evt.destructive_actions);
					updateScope(streamScopeKey, (state) => ({
						...state,
						messages: state.messages.map((m) =>
							m.id === asstMsgId
								? {
										...m,
										proposedPlan: plan,
										planContract: evt.plan_contract,
										intentContract: evt.intent_contract,
										intentGaps: evt.intent_gaps,
									}
								: m,
						),
						lastProposedPlanId: evt.plan_id,
						phase: "Plan ready for review",
						activity: [],
					}));
					didEmit = true;
					return;
				}
				if (evt.type === "plan_updated") {
					const plan = buildProposedPlan(evt.plan_id, evt.name, evt.steps, evt.destructive_actions);
					updateScope(streamScopeKey, (state) => ({
						...state,
						messages: state.messages.map((m) =>
							m.id === asstMsgId
								? {
										...m,
										content:
											"I updated the plan from your answer. Review the edit, then approve it to rerun.",
										proposedPlan: plan,
										planContract: evt.plan_contract,
										intentContract: evt.intent_contract,
										intentGaps: evt.intent_gaps,
										metadata: { ...(m.metadata ?? {}), planDiff: evt.diff },
									}
								: m,
						),
						lastProposedPlanId: evt.plan_id,
						phase: "Plan update ready for review",
						activity: [],
					}));
					// The plan's spec changed under the same id — any earlier run of this plan ran the OLD
					// spec, so its verdict is stale and must not re-anchor under the edited plan card.
					get().invalidateRunsForPlan(evt.plan_id);
					didEmit = true;
					return;
				}
				if (evt.type === "assistant_message") {
					didEmit = true;
					appendActivity(streamScopeKey, {
						kind: "message",
						label: "Assistant response",
						detail: compactActivityText(evt.content),
						status: "ok",
					});
					updateScope(streamScopeKey, (state) => ({
						...state,
						messages: state.messages.map((m) => {
							if (m.id !== asstMsgId) return m;
							// Clear the explore tool field — an assistant_message is a conversational reply
							// (not a tool outcome), so the "tool: explore" accordion should not show.
							const { tool: _tool, ...rest } = m;
							return { ...rest, content: evt.content, metadata: evt.metadata };
						}),
					}));
					return;
				}
				if (evt.type === "usage") {
					// Use the closure's own message id (like every other handler here) —
					// not the global _currentAsstMsgId, which a concurrent preempt could
					// have already repointed at a newer message.
					const asstId = asstMsgId;
					updateScope(streamScopeKey, (state) => ({
						...state,
						messages: state.messages.map((m) =>
							m.id === asstId
								? {
										...m,
										metadata: {
											...(m.metadata ?? {}),
											usage: {
												input: evt.input_tokens,
												output: evt.output_tokens,
												total: evt.total_tokens,
												calls: evt.calls,
											},
										},
									}
								: m,
						),
					}));
					return;
				}
				if (evt.type === "error") {
					appendActivity(streamScopeKey, {
						kind: "error",
						label: "Planner error",
						detail: compactActivityText(evt.error),
						status: "error",
					});
					set({ error: evt.error || "unknown error" });
					return;
				}
				if (evt.type === "done") {
					set({ streaming: false });
					if (!didEmit) {
						updateScope(streamScopeKey, (state) => ({
							...state,
							messages: state.messages.map((m) =>
								m.id === asstMsgId && !m.content ? { ...m, content: "(no response)" } : m,
							),
						}));
					}
					return;
				}
			},
			onError: (err) => {
				set((s) => ({ error: err.message, streaming: false, streamEndTick: s.streamEndTick + 1 }));
			},
			onClose: () => {
				// Snapshot the activity onto the assistant message so each response keeps its own
				// thinking/tool-call history visible after streaming ends.
				updateScope(streamScopeKey, (state) => ({
					...state,
					messages: state.messages.map((m) =>
						m.id === asstMsgId && !m.chatActivity ? { ...m, chatActivity: state.activity } : m,
					),
				}));
				set((s) => ({ streaming: false, streamEndTick: s.streamEndTick + 1 }));
			},
		});
		set({ _abort: abort });
	},

	cancel: () => {
		get()._abort?.();
		const cancelledMsgId = get()._currentAsstMsgId;
		// Snapshot onto the scope the aborted STREAM belonged to — not the currently
		// viewed scope, which may differ (e.g. after a mid-stream promotion or a
		// preempt from another chat). updateScope resolves any promotion redirect.
		const streamScope = get()._streamScope;
		// The run is decoupled from the SSE stream, so aborting the local reader
		// (above) does NOT stop the server-side turn. Tell the backend to stop the
		// run for this session too — best-effort, errors swallowed inside the service.
		const streamSessionId = streamScope ? get().sessionIds[streamScope] : null;
		if (streamSessionId) void stopChatSession(streamSessionId);
		set({ _abort: null, _currentAsstMsgId: null, _streamScope: null });
		if (cancelledMsgId && streamScope) {
			get().updateScope(streamScope, (state) => ({
				...state,
				messages: state.messages.map((m) =>
					m.id === cancelledMsgId && !m.chatActivity ? { ...m, chatActivity: state.activity } : m,
				),
			}));
		}
		set({ streaming: false });
	},

	moveScope: (from, to) => {
		if (!from || !to || from === to) return;
		set((s) => {
			if (!s.byScope[from] || s.byScope[to]) return s;
			const { [from]: moved, ...rest } = s.byScope;
			const sessionIds = { ...s.sessionIds, [to]: s.sessionIds[from] ?? null };
			delete sessionIds[from];
			return {
				byScope: { ...rest, [to]: moved },
				sessionIds,
				// A stream may still be writing to `from` (promotion fires on the
				// plan_proposed event, before the stream ends). Redirect its late
				// events to `to`, and retarget the live-stream pointer, so nothing
				// resurrects the deleted `from` scope.
				_scopeRedirect: { ...s._scopeRedirect, [from]: to },
				_streamScope: s._streamScope === from ? to : s._streamScope,
			};
		});
	},

	resetScope: (key) =>
		set((s) => {
			const { [key]: _dropped, ...redirect } = s._scopeRedirect;
			return {
				byScope: { ...s.byScope, [key]: EMPTY_CHAT_STATE },
				sessionIds: { ...s.sessionIds, [key]: null },
				_scopeRedirect: redirect,
			};
		}),

	hydrateScope: (scopeKey, sessionId, messages, lastProposedPlanId) =>
		set((s) => {
			// Never overwrite a scope already in memory — a live thread, or one hydrated earlier.
			if (!scopeKey || s.byScope[scopeKey]) return s;
			return {
				byScope: {
					...s.byScope,
					[scopeKey]: { messages, lastProposedPlanId, phase: null, activity: [] },
				},
				// Record the server session so a follow-up message continues this conversation.
				sessionIds: { ...s.sessionIds, [scopeKey]: sessionId },
			};
		}),

	setPlanVerification: (planId, verification) =>
		get().updateScope(owningScopeForPlan(planId, get().byScope), (state) => ({
			...state,
			messages: state.messages.map((m) =>
				m.proposedPlan?.id === planId
					? {
							...m,
							proposedPlan: {
								...m.proposedPlan,
								steps: withVerification(m.proposedPlan.steps, verification),
							},
						}
					: m,
			),
		})),

	postRepairPrompt: (scopeKey, runId, text) =>
		get().updateScope(scopeKey, (state) => {
			if (
				state.messages.some(
					(m) => m.metadata?.kind === "repair_prompt" && m.metadata?.runId === runId,
				)
			) {
				return state;
			}
			return {
				...state,
				messages: [
					...state.messages,
					{
						id: `repair-${runId}`,
						role: "assistant",
						content: text,
						metadata: { kind: "repair_prompt", runId },
					},
				],
			};
		}),

	execute: (planId, opts, queryClient) => {
		get()._execAbort?.();
		set({
			execEvents: [],
			execResult: null,
			execError: null,
			_execRunId: null,
			_execPlanId: planId,
			execStreaming: true,
		});
		const abort = streamExecutePlan({
			planId,
			headed: opts?.headed,
			live: opts?.live,
			onEvent: (e) => {
				set((s) => ({ execEvents: [...s.execEvents, e] }));
				if (e.type === "run_started") set({ _execRunId: e.run_id });
				if (e.type === "complete") {
					const { type: _type, ...rest } = e;
					const payload = rest as unknown as ExecuteResult;
					set({
						// Never fall back to "" — an empty run_id collapses distinct runs in
						// the completed-runs dedup and collides as a React key. Mint a
						// unique id if the server + run_started somehow gave us none.
						execResult: {
							...payload,
							run_id: payload.run_id ?? get()._execRunId ?? crypto.randomUUID(),
							// Lift the TRUE outcome so amber states (blocked / degraded / passed_with_retry)
							// survive anywhere `status` — only ever passed|failed|error — is what gets read.
							execution_state: payload.execution_state ?? payload.summary?.execution_state,
						},
						execStreaming: false,
					});
					// The plan's status + its runs changed — refresh so history updates in real time.
					void queryClient.invalidateQueries({ queryKey: ["testingPlans"] });
					void queryClient.invalidateQueries({ queryKey: ["testingRuns", planId] });
					void queryClient.invalidateQueries({ queryKey: ["testingPlan", planId] });
					// Org-config consent: if this run was the verification re-run after applying a change,
					// submit its runId so the server records whether our ORG_CONFIG diagnosis was correct.
					const pv = get()._orgConfigPendingVerdict;
					if (pv && pv.planId === planId) {
						const vRunId = payload.run_id ?? get()._execRunId ?? "";
						set({ _orgConfigPendingVerdict: null });
						if (vRunId) {
							void submitOrgConfigVerdict(planId, pv.changeId, vRunId).then((v) => {
								if (v) set({ orgConfigVerdict: v });
							});
						}
					}
				}
				if (e.type === "error") {
					set({
						execError: new Error((e as { message?: string }).message ?? "Execute failed"),
						execStreaming: false,
					});
				}
			},
			onClose: () => set({ execStreaming: false }),
			onError: (err) => set({ execError: err, execStreaming: false }),
		});
		set({ _execAbort: abort });
	},
	execAbort: () => {
		get()._execAbort?.();
		set({ execStreaming: false });
	},
	execReset: () => {
		get()._execAbort?.();
		set({
			execEvents: [],
			execResult: null,
			execError: null,
			_execRunId: null,
			_execPlanId: null,
			execStreaming: false,
		});
	},

	invalidateRunsForPlan: (planId) =>
		set((s) => ({
			completedRuns: s.completedRuns.filter((r) => r.planId !== planId),
			// Clear the live result too when it is this plan's — otherwise the effect that mirrors
			// `execResult` into `completedRuns` immediately re-adds the just-dropped stale run.
			...(s._execPlanId === planId
				? { execResult: null, execEvents: [], execError: null, _execRunId: null, _execPlanId: null }
				: {}),
		})),

	heal: (runId, queryClient) => {
		get()._healAbort?.();
		set({
			healEvents: [],
			healError: null,
			healStatus: "running",
			healProposal: null,
			healPlanId: null,
			healNotes: [],
			healDeclineReason: null,
			healStage: null,
			healTriageCategory: null,
			_healRunId: runId,
			healStreaming: true,
		});
		const abort = streamHealRun({
			runId,
			onEvent: (e) => {
				set((s) => ({ healEvents: [...s.healEvents, e] }));
				if (e.type === "run_started") set({ _healRunId: e.run_id });
				if (e.type === "heal_proposal_started") {
					set({ _healRunId: e.run_id, healTriageCategory: e.triage_category });
				}
				if (e.type === "heal_progress") set({ healStage: e.stage });
				if (e.type === "heal_proposal") {
					set({
						healProposal: e.proposal,
						healPlanId: e.plan_id,
						_healRunId: e.run_id,
						healStatus: "proposed",
					});
				}
				// The heal stream's `complete` is a PROPOSAL verdict, not a run result — the source
				// run keeps its own verdict, so nothing here may write execResult/healResult.
				if (isHealComplete(e)) {
					const c = e as HealCompleteEvent;
					set({
						healStatus: c.status,
						healProposal: c.proposal ?? get().healProposal,
						healPlanId: c.plan_id ?? get().healPlanId,
						healNotes: c.notes ?? [],
						healDeclineReason: c.status === "declined" ? (c.reason ?? null) : null,
						_healRunId: c.run_id ?? get()._healRunId,
						healStreaming: false,
					});
					// The proposal lives on the plan body — refetch so the plan card can show it.
					if (c.plan_id) {
						void queryClient.invalidateQueries({ queryKey: ["testingPlan", c.plan_id] });
					}
					void queryClient.invalidateQueries({ queryKey: ["testingPlan"] });
				}
				if (e.type === "error") {
					set({
						healError: new Error((e as { message?: string }).message ?? "Heal failed"),
						healStatus: "error",
						healStreaming: false,
					});
				}
			},
			onClose: () =>
				set((s) => ({
					healStreaming: false,
					// Stream ended without a verdict (stall / drop) — don't leave a fake spinner.
					healStatus: s.healStatus === "running" ? "idle" : s.healStatus,
				})),
			onError: (err) =>
				set({
					healError: err,
					// 410 on open = the heal endpoint is disabled server-side, not a failure to report.
					healStatus:
						(err as { status?: number }).status === 410 ? "disabled" : "error",
					healStreaming: false,
				}),
		});
		set({ _healAbort: abort });
	},

	acceptHeal: async (planId, queryClient) => {
		if (!planId || get().healDeciding) return;
		set({ healDeciding: true });
		try {
			const res = await acceptHealProposal(planId);
			if (!res.applied) set({ healError: new Error(res.message ?? "heal not applied") });
		} catch (e) {
			set({ healError: e as Error });
		} finally {
			// Whatever the server said (200, 409, 404) no proposal remains stored — a local copy
			// would be a button that can only fail. Drop it and refetch the plan.
			set({ healDeciding: false, healProposal: null, healPlanId: null, healStatus: "idle" });
			void queryClient.invalidateQueries({ queryKey: ["testingPlan", planId] });
			void queryClient.invalidateQueries({ queryKey: ["testingPlans"] });
			void queryClient.invalidateQueries({ queryKey: ["testingRuns", planId] });
		}
	},

	rejectHeal: async (planId, queryClient) => {
		if (!planId || get().healDeciding) return;
		set({ healDeciding: true });
		try {
			await rejectHealProposal(planId);
		} catch (e) {
			set({ healError: e as Error });
		} finally {
			set({ healDeciding: false, healProposal: null, healPlanId: null, healStatus: "idle" });
			void queryClient.invalidateQueries({ queryKey: ["testingPlan", planId] });
		}
	},

	// ── org-config consent actions ──
	acceptOrgConfig: async (planId, changeId, queryClient) => {
		if (!planId || !changeId || get().orgConfigDeciding) return;
		set({ orgConfigDeciding: true, orgConfigApplyResult: null, orgConfigVerdict: null });
		try {
			const res = await acceptOrgConfigChange(planId, changeId);
			set({ orgConfigApplyResult: res });
			if (res.applied && !res.noOp) {
				// Applied a real change → re-run to verify; execute-complete submits the verdict.
				set({ _orgConfigPendingVerdict: { planId, changeId } });
				get().execute(planId, undefined, queryClient);
			} else if (res.noOp) {
				// The config we said was missing was already present — our diagnosis was wrong; no re-run.
				set({
					orgConfigVerdict: {
						verdict: "incorrect",
						reason: "the org config we diagnosed as missing was already present — nothing to apply",
					},
				});
			}
		} catch (e) {
			set({ orgConfigApplyResult: { applied: false, message: (e as Error).message } });
		} finally {
			set({ orgConfigDeciding: false });
		}
	},

	rejectOrgConfig: async (planId, changeId) => {
		if (!planId || !changeId || get().orgConfigDeciding) return;
		set({ orgConfigDeciding: true });
		try {
			await rejectOrgConfigChange(planId, changeId);
		} finally {
			set({ orgConfigDeciding: false, orgConfigApplyResult: null, orgConfigVerdict: null });
		}
	},

	revertOrgConfig: async (planId, changeId) => {
		if (!planId || !changeId || get().orgConfigDeciding) return;
		set({ orgConfigDeciding: true });
		try {
			const out = await revertOrgConfigChange(planId, changeId);
			set({
				orgConfigApplyResult: { applied: false, message: out.message },
				...(out.reverted ? { orgConfigVerdict: null } : {}),
			});
		} finally {
			set({ orgConfigDeciding: false });
		}
	},

	orgConfigReset: () =>
		set({
			orgConfigDeciding: false,
			orgConfigApplyResult: null,
			orgConfigVerdict: null,
			_orgConfigPendingVerdict: null,
		}),

	healAbort: () => {
		get()._healAbort?.();
		set((s) => ({
			healStreaming: false,
			healStatus: s.healStatus === "running" ? "idle" : s.healStatus,
		}));
	},
	healReset: () => {
		get()._healAbort?.();
		set({
			healEvents: [],
			healError: null,
			healStatus: "idle",
			healProposal: null,
			healPlanId: null,
			healNotes: [],
			healDeclineReason: null,
			healStage: null,
			healTriageCategory: null,
			healDeciding: false,
			_healRunId: null,
			healStreaming: false,
		});
	},

	setSelectedPlanId: (v) =>
		set((s) => {
			const selectedPlanId = apply(v, s.selectedPlanId);
			// Persist so a hard refresh restores the selection (→ run reconnect).
			writePersistedTestingSelection(selectedPlanId);
			return { selectedPlanId };
		}),
	setEditingPlanId: (v) => set((s) => ({ editingPlanId: apply(v, s.editingPlanId) })),
	setActiveChatScope: (v) => set((s) => ({ activeChatScope: apply(v, s.activeChatScope) })),
	setCompletedRuns: (v) => set((s) => ({ completedRuns: apply(v, s.completedRuns) })),
	setHistoryPlanId: (v) => set((s) => ({ historyPlanId: apply(v, s.historyPlanId) })),
	setLiveExpanded: (v) => set((s) => ({ liveExpanded: apply(v, s.liveExpanded) })),

	ensureUser: (uid) => {
		if (get().uid === uid) return;
		get()._abort?.();
		get()._execAbort?.();
		get()._healAbort?.();
		set({ uid, ...FRESH });
	},
}));

// ─── pure helpers ─────────────────────────────────────────────────────────────

/** The VERIFY verdict carried on an IR plan body (`steps.verification`) — sourced from the schema. */
export type PlanVerification = NonNullable<IrPlanBody["verification"]>;

/**
 * Attach a verify verdict to a plan body. Verification only applies to IR plans (the only kind the
 * verify endpoint runs); a spec or legacy body is returned unchanged.
 */
function withVerification(steps: PlanBody, verification: PlanVerification): PlanBody {
	return isIrPlan(steps) ? { ...steps, verification } : steps;
}

function buildProposedPlan(
	id: string,
	name: string,
	steps: PlanBody,
	destructiveActions?: DestructiveAction[],
): TestPlan {
	return {
		id,
		creator_user_id: "",
		customer_org_id: "",
		name,
		intent: null,
		steps,
		target_kind: "connected",
		status: "proposed",
		is_template: false,
		visibility: "private",
		tags: [],
		created_at: new Date().toISOString(),
		updated_at: new Date().toISOString(),
		// Carry the planner's destructive-action list onto the snapshot so the RUN
		// confirmation gate can read it even before the live plan record loads.
		destructive_actions: destructiveActions ?? [],
	};
}

function normalizeRuntimeActivity(
	evt: RuntimeActivityEvent,
): Omit<TestingChatActivity, "id" | "at"> {
	return {
		kind: evt.kind,
		label: compactActivityText(evt.label) ?? evt.label,
		detail: compactActivityText(evt.detail ?? evt.tool ?? summarizeToolData(evt.data) ?? ""),
		status: evt.status,
		tool: evt.tool,
		data: evt.data,
	};
}

function summarizeToolData(data: unknown): string | undefined {
	if (data == null) return undefined;
	if (typeof data === "string") return data.slice(0, 160);
	if (Array.isArray(data)) return `${data.length} item${data.length === 1 ? "" : "s"}`;
	if (typeof data === "object") {
		const obj = data as Record<string, unknown>;
		const keys = Object.keys(obj).slice(0, 5);
		if (keys.length === 0) return undefined;
		return keys
			.map((key) => {
				const value = obj[key];
				if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
					return `${key}: ${String(value)}`;
				}
				if (Array.isArray(value)) return `${key}: ${value.length} items`;
				return key;
			})
			.join(" · ")
			.slice(0, 160);
	}
	return String(data).slice(0, 160);
}

function compactActivityText(value: string): string | undefined {
	const text = value.replace(/\s+/g, " ").trim();
	if (!text) return undefined;
	return text.length > 96 ? `${text.slice(0, 93)}...` : text;
}

// ── Organization scoping ─────────────────────────────────────────────────────
//
// Test plans, runs, heal proposals and chat threads are all keyed to one
// organization's Salesforce metadata. This store already reset itself when a
// different USER took over the tab (`ensureUser`); the same argument applies to
// a different ORGANIZATION, and it was missing.
//
// Three live streams may be attached (chat, execution, heal). They are aborted
// rather than parked: unlike an agent run, a test execution drives a server-side
// browser session that this tab is the only consumer of, and leaving three SSE
// connections open per organization visited is not a cost worth paying for state
// the plan list rebuilds on return.
//
// The incoming organization's persisted plan selection is restored, so switching
// back lands on the plan the user was last looking at there — the same behaviour
// a page reload already gave them.
registerOrgScopedReset("testingStore", () => {
	const s = useTestingStore.getState();
	s._abort?.();
	s._execAbort?.();
	s._healAbort?.();
	useTestingStore.setState({
		...FRESH,
		selectedPlanId: readPersistedTestingSelection().selectedPlanId,
	});
});
